import { useState, useEffect } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { Boxes, FileCode2, GitCompareArrows, History, Layers, ShieldCheck, BadgeCheck, Map, Sun, Moon, Hammer, Activity, Mountain, FolderKanban, Settings2, LogOut, Sparkles, BookOpen } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { PoweredBy, LegalLinks } from "@/components/Branding";

const NAV = [
  { to: "/", label: "Library", icon: Layers, end: true, id: "nav-library" },
  { to: "/projects", label: "Projects", icon: FolderKanban, id: "nav-projects", pro: true },
  { to: "/builder", label: "Builder", icon: Hammer, id: "nav-builder", pro: true },
  { to: "/reader", label: "Reader", icon: FileCode2, id: "nav-reader" },
  { to: "/validate", label: "Validate", icon: BadgeCheck, id: "nav-validate" },
  { to: "/compare", label: "Compare", icon: GitCompareArrows, id: "nav-compare", pro: true },
  { to: "/qa", label: "QA Tolerance", icon: ShieldCheck, id: "nav-qa", pro: true },
  { to: "/map", label: "Map", icon: Map, id: "nav-map", pro: true },
  { to: "/profile", label: "Profile", icon: Activity, id: "nav-profile", pro: true },
  { to: "/surfaces", label: "Surfaces", icon: Mountain, id: "nav-surfaces", pro: true },
  { to: "/history", label: "History", icon: History, id: "nav-history" },
  { to: "/settings", label: "Setup", icon: Settings2, id: "nav-settings" },
  { to: "/help", label: "Help", icon: BookOpen, id: "nav-help" },
];

export default function AppLayout() {
  const [theme, setTheme] = useState(() => localStorage.getItem("adac-theme") || "dark");
  const { projects, activeId, setActiveId } = useProject();
  const { user, isPro, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => { await logout(); nav("/login", { replace: true }); };

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("adac-theme", theme);
  }, [theme]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-zinc-100">
      <aside className="w-60 shrink-0 border-r border-zinc-800 bg-[#0C0C0C] flex flex-col fixed h-screen">
        <Link
          to="/"
          data-testid="brand-logo"
          className="h-16 flex items-center gap-3 px-5 border-b border-zinc-800"
        >
          <div className="w-8 h-8 rounded-sm bg-blue-600 flex items-center justify-center">
            <Boxes className="w-5 h-5 text-white" />
          </div>
          <div className="leading-none">
            <div className="font-display font-bold text-sm tracking-tight">ADAC</div>
            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">XML DIFF SUITE</div>
          </div>
        </Link>

        <div className="px-3 py-3 border-b border-zinc-800" data-testid="project-switcher-wrap">
          <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mb-1.5 px-1 flex items-center justify-between">
            <span>Active project</span>
            <Link to="/projects" className="text-zinc-600 hover:text-blue-400 transition-colors duration-75" data-testid="manage-projects-link">manage</Link>
          </div>
          <Select value={activeId || "none"} onValueChange={(v) => setActiveId(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="project-switcher" className="h-8 rounded-sm bg-[#0A0A0A] border-zinc-800 font-mono text-[11px]">
              <SelectValue placeholder="No project" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-72">
              <SelectItem value="none" className="font-mono text-[11px]">— All files (no project) —</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id} className="font-mono text-[11px]">{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-75 ${
                  isActive
                    ? "bg-blue-600/15 text-blue-400 border border-blue-600/30"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800 space-y-3">
          {!isPro && (
            <div data-testid="free-usage-meter" className="rounded-sm border border-zinc-800 bg-[#0A0A0A] px-3 py-2">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                <span>Free exports</span>
                <span className="text-zinc-300">{Math.min(user?.usage_count ?? 0, user?.usage_limit ?? 3)} / {user?.usage_limit ?? 3}</span>
              </div>
              <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${Math.min(100, ((user?.usage_count ?? 0) / (user?.usage_limit ?? 3)) * 100)}%` }}
                />
              </div>
            </div>
          )}
          {!isPro && (
            <Link to="/pricing" data-testid="nav-upgrade" className="flex items-center justify-center gap-2 px-3 py-2 rounded-sm bg-blue-600 hover:bg-blue-500 text-white font-mono text-[11px] uppercase tracking-wider transition-colors duration-75">
              <Sparkles className="w-3.5 h-3.5" /> View plans
            </Link>
          )}
          <div className="flex items-center gap-2 px-1" data-testid="user-area">
            <div className="w-7 h-7 rounded-sm bg-zinc-800 flex items-center justify-center font-mono text-[11px] text-zinc-300 shrink-0 uppercase">
              {(user?.name || user?.email || "?").slice(0, 1)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[11px] text-zinc-200 truncate" data-testid="user-email">{user?.email}</div>
              <div className="font-mono text-[9px] uppercase tracking-wider">
                <span className={isPro ? "text-emerald-400" : "text-zinc-500"}>{user?.plan} plan</span>
              </div>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" title="Sign out" className="text-zinc-500 hover:text-red-400 transition-colors duration-75 p-1"><LogOut className="w-4 h-4" /></button>
          </div>
          <button
            data-testid="theme-toggle"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            className="w-full flex items-center justify-between px-3 py-2 rounded-sm border border-zinc-800 hover:bg-zinc-900 transition-colors duration-75 text-zinc-400"
          >
            <span className="flex items-center gap-2 font-mono text-[11px]">
              {theme === "light" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              {theme === "light" ? "LIGHT" : "DARK"}
            </span>
            <span className="font-mono text-[10px] text-zinc-600">toggle</span>
          </button>
          <div className="flex items-center justify-between pt-1">
            <PoweredBy />
            <LegalLinks />
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-60 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
